import React, { useState, useRef, useEffect } from 'react';
import {
  Bot, Send, Loader2, Sparkles, Database, TrendingUp,
  Package, Users, DollarSign, AlertTriangle, RefreshCw,
  MessageSquare, Trash2, ChevronDown
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api/axios';
import useAuthStore from '../store/authStore';
import { formatUiCurrencyCompact } from '../utils/uiFormat';

const AIAssistant = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [context, setContext] = useState(null);
  const [showContext, setShowContext] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const { user, hasPermission } = useAuthStore();
  const hasAccess = hasPermission('ai_assistant', 'view');

  const fetchContext = async () => {
    try {
      const res = await api.get('/ai/context/');
      setContext(res.data);
    } catch (error) {
      console.error('Failed to fetch context:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (!hasAccess) return;
    fetchContext();
    setMessages([{
      role: 'assistant',
      content: `Hello! 👋 I'm your AI Assistant. I can help you with:\n\n• **Sales & Revenue** - Today's sales, weekly/monthly reports\n• **Inventory** - Low stock alerts, expiring items\n• **Menu** - Top selling items, categories\n• **Expenses & Wastage** - Cost analysis\n• **Staff** - User management info\n\nAsk me anything about your business!`
    }]);
  }, [hasAccess]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  if (!hasAccess) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Bot className="mx-auto text-gray-400 mb-4" size={64} />
          <h2 className="text-xl font-semibold text-gray-700">Access Denied</h2>
          <p className="text-gray-500 mt-2">You don't have permission to access the AI Assistant.</p>
        </div>
      </div>
    );
  }

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    
    // Add user message to chat
    const newMessages = [...messages, { role: 'user', content: userMessage }];
    setMessages(newMessages);
    setLoading(true);

    try {
      const response = await api.post('/ai/chat/', {
        message: userMessage,
        history: newMessages.slice(-10).map(m => ({
          role: m.role,
          content: m.content
        }))
      });

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.response,
        usage: response.data.usage
      }]);
    } catch (error) {
      console.error('AI Error:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.details || 'Failed to get response from AI';
      toast.error(errorMessage);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Sorry, there was an error: ${errorMessage}. Please try again.`,
        isError: true
      }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([{
      role: 'assistant',
      content: 'Chat cleared! How can I help you?'
    }]);
  };

  const quickQuestions = [
    { icon: TrendingUp, text: "Show today's complete item-wise sales breakdown", color: "bg-green-100 text-green-700" },
    { icon: Package, text: "Show low stock items", color: "bg-orange-100 text-orange-700" },
    { icon: DollarSign, text: "What's this month's profit?", color: "bg-blue-100 text-blue-700" },
    { icon: AlertTriangle, text: "Which items sold most today and which didn't sell?", color: "bg-red-100 text-red-700" },
  ];

  const escapeHtml = (text) => {
    const div = document.createElement('div');
    div.appendChild(document.createTextNode(text));
    return div.innerHTML;
  };

  const formatMessage = (content) => {
    // Sanitize first to prevent XSS
    const safe = escapeHtml(content);
    return safe
      .split('\n')
      .map((line, i) => {
        // Bold text
        line = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        // Bullet points
        if (line.startsWith('• ') || line.startsWith('- ')) {
          return `<li key=${i} class="ml-4">${line.substring(2)}</li>`;
        }
        return line;
      })
      .join('<br/>');
  };

  const formatMoney = (value) => formatUiCurrencyCompact(value, context?.shop?.currency || 'Rs.');

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-t-xl p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Bot className="text-white" size={28} />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
                AI Assistant
                
              </h1>
              <p className="text-primary-100 text-sm">Powered by GPT-4o Mini • Full Database Access</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowContext(!showContext)}
              className="flex items-center gap-2 px-3 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-white text-sm transition"
            >
              <Database size={16} />
              <span className="hidden sm:inline">Database Context</span>
              <ChevronDown size={16} className={`transition ${showContext ? 'rotate-180' : ''}`} />
            </button>
            <button
              onClick={fetchContext}
              className="p-2 bg-white/20 hover:bg-white/30 rounded-lg text-white transition"
              title="Refresh Context"
            >
              <RefreshCw size={18} />
            </button>
            <button
              onClick={clearChat}
              className="p-2 bg-white/20 hover:bg-white/30 rounded-lg text-white transition"
              title="Clear Chat"
            >
              <Trash2 size={18} />
            </button>
          </div>
        </div>

        {/* Context Panel */}
        {showContext && context && (
          <div className="mt-4 bg-white/10 rounded-lg p-4 text-white text-sm">
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
              <div>
                <p className="text-primary-200 text-xs">Today's Sales</p>
                <p className="text-base sm:text-xl font-bold">{formatMoney(context.sales?.today?.revenue || 0)}</p>
                <p className="text-xs text-primary-200">{context.sales?.today?.orders || 0} orders</p>
              </div>
              <div>
                <p className="text-primary-200 text-xs">Avg Order</p>
                <p className="text-base sm:text-xl font-bold">{formatMoney(context.sales?.today?.avg_order_value || 0)}</p>
                <p className="text-xs text-green-300">per order</p>
              </div>
              <div>
                <p className="text-primary-200 text-xs">Month Profit</p>
                <p className="text-base sm:text-xl font-bold">{formatMoney(context.profit?.month_net || 0)}</p>
                <p className="text-xs text-green-300">{context.profit?.profit_margin_percent || 0}% margin</p>
              </div>
              <div>
                <p className="text-primary-200 text-xs">Inventory</p>
                <p className="text-base sm:text-xl font-bold">{context.inventory?.total_items || 0} items</p>
                <p className="text-xs text-red-300">{context.inventory?.low_stock_count || 0} low, {context.inventory?.out_of_stock || 0} out</p>
              </div>
              <div>
                <p className="text-primary-200 text-xs">Expenses</p>
                <p className="text-base sm:text-xl font-bold">{formatMoney(context.expenses?.month_total || 0)}</p>
                <p className="text-xs text-orange-300">{formatMoney(context.expenses?.today_total || 0)} today</p>
              </div>
              <div>
                <p className="text-primary-200 text-xs">Wastage</p>
                <p className="text-base sm:text-xl font-bold">{context.wastage?.pending_approval || 0} pending</p>
                <p className="text-xs text-red-300">{formatMoney(context.wastage?.month_cost || 0)} cost</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Quick Questions */}
      {messages.length <= 1 && (
        <div className="bg-white border-b p-4">
          <p className="text-sm text-gray-500 mb-3">Quick questions:</p>
          <div className="flex flex-wrap gap-2">
            {quickQuestions.map((q, i) => (
              <button
                key={i}
                onClick={() => {
                  const msg = q.text;
                  setInput('');
                  // Directly send the quick question
                  const newMessages = [...messages, { role: 'user', content: msg }];
                  setMessages(newMessages);
                  setLoading(true);
                  api.post('/ai/chat/', {
                    message: msg,
                    history: newMessages.slice(-10).map(m => ({ role: m.role, content: m.content }))
                  }).then(response => {
                    setMessages(prev => [...prev, {
                      role: 'assistant',
                      content: response.data.response,
                      usage: response.data.usage
                    }]);
                  }).catch(error => {
                    const errorMessage = error.response?.data?.error || error.response?.data?.details || 'Failed to get response';
                    toast.error(errorMessage);
                    setMessages(prev => [...prev, {
                      role: 'assistant',
                      content: `Sorry, there was an error: ${errorMessage}`,
                      isError: true
                    }]);
                  }).finally(() => setLoading(false));
                }}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition hover:scale-105 ${q.color}`}
              >
                <q.icon size={16} />
                {q.text}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto bg-gray-50 p-4 space-y-4">
        {messages.map((message, index) => (
          <div
            key={index}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] sm:max-w-[75%] rounded-2xl px-4 py-3 ${
                message.role === 'user'
                  ? 'bg-primary-600 text-white rounded-br-md'
                  : message.isError
                  ? 'bg-red-100 text-red-800 rounded-bl-md'
                  : 'bg-white shadow-sm border border-gray-100 rounded-bl-md'
              }`}
            >
              {message.role === 'assistant' && (
                <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-100">
                  <Bot size={18} className="text-primary-600" />
                  <span className="text-xs font-medium text-primary-600">AI Assistant</span>
                </div>
              )}
              <div
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: formatMessage(message.content) }}
              />
              {message.usage && (
                <p className="text-xs text-gray-400 mt-2">
                  Tokens: {message.usage.total_tokens}
                </p>
              )}
            </div>
          </div>
        ))}
        
        {loading && (
          <div className="flex justify-start">
            <div className="bg-white shadow-sm border border-gray-100 rounded-2xl rounded-bl-md px-4 py-3">
              <div className="flex items-center gap-2">
                <Loader2 className="animate-spin text-primary-600" size={20} />
                <span className="text-gray-500">AI is thinking...</span>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="bg-white border-t p-4">
        <div className="flex items-end gap-2">
          <div className="flex-1 relative">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask anything about your business..."
              className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none text-sm sm:text-base"
              rows={1}
              style={{ minHeight: '48px', maxHeight: '120px' }}
              disabled={loading}
            />
            <div className="absolute right-2 bottom-2 text-xs text-gray-400">
              {input.length > 0 && `${input.length}/1000`}
            </div>
          </div>
          <button
            onClick={sendMessage}
            disabled={!input.trim() || loading}
            className={`p-3 rounded-xl transition ${
              input.trim() && !loading
                ? 'bg-primary-600 hover:bg-primary-700 text-white'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            {loading ? (
              <Loader2 className="animate-spin" size={22} />
            ) : (
              <Send size={22} />
            )}
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-2 text-center">
          <MessageSquare size={12} className="inline mr-1" />
          AI has access to your complete database • Admin only
        </p>
      </div>
    </div>
  );
};

export default AIAssistant;
